import { z } from 'zod';

/* ------------------------------------------------------------------ *
 * Localization
 * ------------------------------------------------------------------ */

/**
 * Locales shipped with the platform. Adding a locale = adding a JSON
 * bundle in `apps/api/src/i18n/locales/<code>` and
 * `apps/web/src/shared/i18n/locales/<code>` plus one entry here.
 *
 * The product is Argentine, so Spanish comes first and is the default. The
 * country is home to communities that speak other languages — English and
 * Russian are kept as first-class interface languages, and the list order is
 * the order the language switcher renders top to bottom.
 */
export const LOCALES = ['es', 'en', 'ru'] as const;
export const LocaleSchema = z.enum(LOCALES);
export type Locale = (typeof LOCALES)[number];

/**
 * Product default. The interface still prefers the operating system's
 * language (see `resolveInitialLocale` in the web client) — this is only what
 * an unknown or unsupported system language falls back to.
 */
export const DEFAULT_LOCALE: Locale = 'es';
export const FALLBACK_LOCALE: Locale = 'es';

/**
 * Display names, each written in its own language so a speaker can recognise
 * it without already understanding the current interface language.
 */
export const LOCALE_LABELS: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
  ru: 'Русский',
};

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/* ------------------------------------------------------------------ *
 * Roles (RBAC)
 * ------------------------------------------------------------------ */

export const ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'STOREKEEPER', 'CASHIER', 'VIEWER'] as const;
export const RoleSchema = z.enum(ROLES);
export type Role = (typeof ROLES)[number];

/** Higher number => more authority. Used for "at least this role" checks. */
export const ROLE_RANK: Record<Role, number> = {
  VIEWER: 10,
  CASHIER: 20,
  STOREKEEPER: 30,
  MANAGER: 40,
  ADMIN: 60,
  OWNER: 100,
};

/** Permissions are strings so they can be extended without a migration. */
export const PERMISSIONS = [
  'catalog:read',
  'catalog:write',
  'warehouse:read',
  'warehouse:write',
  'stock:read',
  'stock:write',
  'document:read',
  'document:write',
  'document:post',
  'counterparty:read',
  'counterparty:write',
  'user:read',
  'user:write',
  'settings:write',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  VIEWER: ['catalog:read', 'warehouse:read', 'stock:read', 'document:read', 'counterparty:read'],
  CASHIER: [
    'catalog:read',
    'warehouse:read',
    'stock:read',
    'document:read',
    'document:write',
    'counterparty:read',
  ],
  STOREKEEPER: [
    'catalog:read',
    'warehouse:read',
    'stock:read',
    'stock:write',
    'document:read',
    'document:write',
    'document:post',
    'counterparty:read',
  ],
  MANAGER: [
    'catalog:read',
    'catalog:write',
    'warehouse:read',
    'warehouse:write',
    'stock:read',
    'stock:write',
    'document:read',
    'document:write',
    'document:post',
    'counterparty:read',
    'counterparty:write',
    'user:read',
  ],
  ADMIN: [...PERMISSIONS],
  OWNER: [...PERMISSIONS],
};

/**
 * Fail closed.
 *
 * A role the table does not know about — a value from a newer client, or a
 * row edited by hand — must deny. Indexing the table directly would throw,
 * turning a routine authorization check into a 500 that both leaks the
 * inconsistency and tells the caller the request got further than it should.
 */
export const roleHasPermission = (role: Role | string, permission: Permission): boolean =>
  (ROLE_PERMISSIONS[role as Role] ?? []).includes(permission);

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

export const IdSchema = z.string().min(1).max(64);

/**
 * Decimal values cross the wire as strings so that money/quantity never
 * suffers IEEE-754 rounding. Clients may send numbers for convenience.
 */
export const DecimalSchema = z
  .union([z.string(), z.number()])
  .transform((value) => (typeof value === 'number' ? String(value) : value.trim()))
  .refine((value) => /^-?\d+(\.\d+)?$/.test(value), {
    message: 'validation.decimal',
  });

export const PositiveDecimalSchema = DecimalSchema.refine((value) => Number(value) > 0, {
  message: 'validation.positive',
});

export const NonNegativeDecimalSchema = DecimalSchema.refine((value) => Number(value) >= 0, {
  message: 'validation.nonNegative',
});

/** Currency code (ISO-4217). */
export const CurrencySchema = z
  .string()
  .trim()
  .length(3)
  .transform((value) => value.toUpperCase());

export type Currency = z.infer<typeof CurrencySchema>;

/**
 * Country code (ISO 3166-1 alpha-2), uppercase.
 *
 * The country a company operates in — which is *not* the same axis as the
 * language its interface is in, nor the currency it bills in. Spanish is
 * spoken in nineteen countries, Brazil bills in reais, and an Argentine
 * accountant may well prefer an English interface.
 *
 * It is what selects the country pack, so it decides tax rates, identity
 * validation, document types and the seeded reference data. No type alias is
 * exported alongside it to avoid colliding with `CountryCode` from
 * `@arriba.digital/countries`, which is the pack's own notion of the same value.
 */
export const CountryCodeSchema = z
  .string()
  .trim()
  .length(2)
  .transform((value) => value.toUpperCase());

/**
 * Currencies offered in the interface. The schema itself accepts any
 * three-letter ISO-4217 code, so a tenant whose currency is missing here can
 * still be set through the API — this list only drives the picker.
 *
 * The Argentine peso leads the list because it is the currency of the market
 * the product was built for; the rest are the currencies a Latin American
 * company actually invoices in or is invoiced in.
 */
export const CURRENCIES = [
  'ARS',
  'BRL',
  'CLP',
  'MXN',
  'COP',
  'PEN',
  'UYU',
  'PYG',
  'BOB',
  'USD',
  'EUR',
  'GBP',
  'CNY',
] as const;

/**
 * Fallbacks used only when a country pack has not been applied yet — a tenant
 * in `ONBOARDING`, which has no database and cannot do anything. Once a
 * country is chosen, the pack supplies all three: see `CountryPack.defaults`.
 */
export const DEFAULT_CURRENCY = 'ARS';

/** @see DEFAULT_CURRENCY */
export const DEFAULT_TIMEZONE = 'America/Argentina/Buenos_Aires';

export const SortOrderSchema = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrderSchema>;

export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().trim().max(200).optional(),
  sort: z.string().trim().max(64).optional(),
  order: SortOrderSchema.default('desc'),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;
/**
 * Input shape of the corresponding query type.
 *
 * `z.infer` reflects the schema's *output*, where `.default()` has already
 * been applied — so `page` and `order` look required. Callers legitimately
 * omit them, so the client-facing types use `z.input` instead.
 */
export type PaginationQueryInput = z.input<typeof PaginationQuerySchema>;

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
  pageCount: number;
}

export const paginatedSchema = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    perPage: z.number().int().positive(),
    pageCount: z.number().int().nonnegative(),
  });

/* ------------------------------------------------------------------ *
 * Concurrency
 * ------------------------------------------------------------------ */

/**
 * Optimistic-concurrency token.
 *
 * Every mutable record carries a counter that the database increments on each
 * write. A client reads it with the record and sends it back with the write;
 * the server refuses a write whose counter has moved on. Without it two people
 * editing the same draft produce a silent last-write-wins: the second save
 * discards the first, and nobody is told.
 *
 * An integer rather than a timestamp, because a timestamp is a clock and two
 * writes can share a millisecond; `updatedAt` remains a human-facing fact and
 * is never the thing a conflict is decided on.
 */
export const VersionSchema = z.number().int().nonnegative();
export type Version = z.infer<typeof VersionSchema>;

/** A response body that carries the row's current version. */
export const VersionedSchema = z.object({ version: VersionSchema });

/**
 * Who changed the record, in the `details` of a 409.
 *
 * The point is the name and the moment: "somebody changed this" leaves the
 * user with nowhere to go, while "Ana Ruiz changed this two minutes ago"
 * tells them who to ask. Every field is optional because the record may have
 * been deleted instead of updated, and the author's account may since have
 * been removed.
 */
export const ConflictDetailsSchema = z.object({
  /** `stale` — the record moved on; `deleted` — it is no longer there. */
  conflict: z.enum(['stale', 'deleted']),
  /** What the server holds now. */
  currentVersion: VersionSchema.optional(),
  /** What the caller sent, so the client can tell how far behind it is. */
  expectedVersion: VersionSchema.optional(),
  /** `null` when the author's account has since been removed. */
  changedByName: z.string().nullable().optional(),
  /** ISO-8601 moment of the winning write. */
  changedAt: z.string().optional(),
});
export type ConflictDetails = z.infer<typeof ConflictDetailsSchema>;

/* ------------------------------------------------------------------ *
 * Error envelope
 * ------------------------------------------------------------------ */

/**
 * Every failure returns this shape. `code` is a stable machine-readable
 * key (e.g. `catalog.product.notFound`) that the PWA maps to a localized
 * string, while `message` is already localized server-side using the
 * `Accept-Language` header.
 */
export const ApiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  timestamp: z.string(),
  path: z.string(),
  requestId: z.string().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/* ------------------------------------------------------------------ *
 * Health
 * ------------------------------------------------------------------ */

export const HealthSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  version: z.string(),
  uptimeSeconds: z.number().nonnegative(),
  controlDatabase: z.enum(['up', 'down']),
  tenants: z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
  }),
});
export type Health = z.infer<typeof HealthSchema>;
